import { Component, OnInit, OnDestroy, state, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  FieldDefinition,
  BeneficiaryPayload,
  ErrorResponse,
  SuccessResponse
} from './merchant.models';
import { Merchant, MerchantStateStore, State } from './merchant.state.store';
import { Router, ActivatedRoute } from '@angular/router';
import { take, catchError, filter, switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AuthService } from '../user/auth.service';
import { environment } from '../../environments/environment';
import { RegistryStateStore } from '../registry/registry.state.store';
import { Registry } from '../registry/registry';
import { RegistryFirebaseService } from '../registry/registry-firebase.service';

interface BeneficiaryResponse {
  data: {
    beneficiary: {
      first_name: string;
      last_name: string;
      entity_type: string;
      bank_details: {
        bank_country_code: string;
        account_currency: string;
        account_number: string;
        account_name: string;
        account_routing_type1?: string;
        account_routing_value1?: string;
        iban?: string;
      };
      address?: {
        country_code: string;

        street_address: string;
        city: string;
        state: string;
        postcode: string;
      };
    };
  };
}

@Component({
  selector: 'app-merchant',
  templateUrl: './merchant.component.html',
  styleUrls: ['./merchant.component.scss']
})
export class MerchantComponent implements OnInit, OnDestroy {
  dynamicForm: FormGroup;
  schemaJson: string = '';
  fields: FieldDefinition[] = [];
  selectedCountry: string = '';
  selectedCurrency: string = '';
  isLoading: boolean = false;
  formErrors: { [key: string]: string } = {};
  successMessage: string = '';
  isSubmitting: boolean = false;
  private merchantSubscription: Subscription | null = null;
  private userId: string;
  error: boolean = false;
  errorMessage: string = '';
  registry: Registry;
  @Input() readonly: boolean = false;
  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private merchantStateStore: MerchantStateStore,
    private router: Router,
    private authService: AuthService,
    private _registryStateStore: RegistryStateStore,
    private _registryFirebaseService: RegistryFirebaseService,
    private route: ActivatedRoute
  ) {
    this.dynamicForm = this.fb.group({});
  }

  ngOnInit() {
    document.title = 'Hitchd | Merchant Settings';

    // First, try to get registry data from route snapshot
    this.registry = this.route.snapshot.data.registry;
    if (this.registry && this.registry.country) {
      this.selectedCountry = this.registry.country;
      this.selectedCurrency = this.registry.currency;
      this.setCurrency(); // This will set the correct currency based on country
    }

    this.isLoading = true;
    this.merchantSubscription = this.authService.user.pipe(
      take(1),
      filter(user => !!user),
      switchMap(user => {
        this.userId = user.uid;
        console.log('userId', this.userId);
        return this.merchantStateStore.getMerchant(user.uid);
      }),
      catchError(err => {
        console.error('Error fetching merchant data:', err);
        this.isLoading = true;

        throw err;
      })
    ).subscribe({
      next: (merchant: Merchant | null) => {

        if (!merchant) {

          if (this.router.url.endsWith('/update')) {
            // No merchant data - redirect to add
            this.router.navigate(['/registry/add-payment-methods/credit-card/add']);
          } else {
            // Proceed with default schema for add but only if the registry is paid
            this.registry = this.route.snapshot.data.registry;

            this._registryFirebaseService.registryByUserId(this.registry.userId).pipe(take(1)).subscribe(registry => {
              if (registry && registry.paid) {
                this.selectedCountry = registry.country;
                this.selectedCurrency = registry.currency;
                this.fetchSchema();

              } else {
                this.router.navigate(['/registry/payment']);
              }
            });

            if (this.registry && this.registry.paid) {
              this.fetchSchema();
            } else {
              // If registry is not paid, redirect to payment page
              this.router.navigate(['/registry/payment']);
            }
          }
          return;
        }

        if (this.router.url.endsWith('/add')) {
          // Merchant exists - redirect to update
          this.router.navigate(['/registry/add-payment-methods/credit-card/update']);
        } else {
          // Update route with merchant data
          this.selectedCountry = merchant.schemaCountry;
          this.selectedCurrency = merchant.schemaCurrency;
          this.loadUpdateMode(merchant.merchantId);
        }
      },
      error: () => {
        // On error, try to proceed with default schema
        this.isLoading = false;
        this.fetchSchema();
      }
    });
  }

  ngOnDestroy() {
    if (this.merchantSubscription) {
      this.merchantSubscription.unsubscribe();
    }
  }

  async fetchSchema() {
    try {
      const payload = {
        bank_country_code: this.selectedCountry,
        account_currency: this.selectedCurrency,
        entity_type: 'PERSONAL',
        transfer_method: 'LOCAL'
      };
      this.isLoading = true;
      const response = await this.http.post(
        environment.firebase.clouldFunctionHost + `/getSchema`,
        payload  // Send payload in the body instead of params
      ).toPromise();



      this.schemaJson = JSON.stringify(response, null, 2);
      this.generateForm(response);
      this.isLoading = false;
    } catch (error) {
      console.error('Error fetching schema:', error);
      this.error = true;
      this.isLoading = false;
    }
  }

  onCountryChange(event: any) {
    if (this.readonly) {
      return;
    }
    this.selectedCountry = event.target.value;
    this.setCurrency();
    this.dynamicForm.reset();  // Reset form before fetching new schema
    this.fetchSchema(); // Fetch new schema when country changes
  }

  setCurrency() {
    // Set default currency based on country
    const prevCurrency = this.selectedCurrency;

    switch (this.selectedCountry) {
      case 'AU':
        this.selectedCurrency = 'AUD';
        break;
      case 'GB':
        this.selectedCurrency = 'GBP';
        break;
      case 'US':
        this.selectedCurrency = 'USD';
        break;
      case 'CA':
        this.selectedCurrency = 'CAD';
        break;
      case 'HK':
        this.selectedCurrency = 'HKD';
        break;
      case 'NZ':
        this.selectedCurrency = 'NZD';
        break;
      case 'SG':
        this.selectedCurrency = 'SGD';
        break;
      case 'SE':
        this.selectedCurrency = 'SEK';
        break;
      case 'NO':
        this.selectedCurrency = 'NOK';
        break;
      case 'DK':
        this.selectedCurrency = 'DKK';
        break;
      case 'CH':
        this.selectedCurrency = 'CHF';
        break;
      case 'MX':
        this.selectedCurrency = 'MXN';
        break;
      default:
        if (['AT', 'BE', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'NL', 'PT', 'SK', 'SI', 'ES'].includes(this.selectedCountry)) {
          this.selectedCurrency = 'EUR';
        }
    }

    if (prevCurrency !== this.selectedCurrency) {
      this.dynamicForm.reset();  // Reset form if currency changed
      this.fetchSchema(); // Fetch new schema when currency changes
    }
  }

  updateForm() {
    if (this.readonly) {
      return;
    }
    if (this.dynamicForm) {
      this.dynamicForm.patchValue({
        bank_country_code: this.selectedCountry,
        account_currency: this.selectedCurrency,
        country_code: this.selectedCountry
      });
    }
  }

  onSchemaInput(event: any) {
    try {
      const schema = JSON.parse(event.target.value);
      this.schemaJson = event.target.value;
      this.generateForm(schema);
    } catch (e) {
      console.error('Invalid JSON schema');
    }
  }

  generateForm(schema: any) {
    if (!schema || !schema.data || !schema.data.fields) {
      return;
    }

    // Reset any existing form data
    this.fields = [];
    this.formErrors = {};
    this.successMessage = '';

    const hiddenFields = [
      'bank_country_code',
      'account_currency',
      'transfer_method',
      'entity_type',
      'country_code',
      'account_routing_type1',
      'account_routing_type2'
    ];

    // Store values from schema if they exist
    let localClearingSystem = '';
    let accountRoutingType = '';
    let accountRoutingValue = '';

    const clearingSystemField = schema.data.fields.find(
      (field: FieldDefinition) => field.field.key === 'local_clearing_system'
    );
    if (clearingSystemField) {
      localClearingSystem = clearingSystemField.field.default || '';
    }

    const routingTypeField = schema.data.fields.find(
      (field: FieldDefinition) => field.field.key === 'account_routing_type1'
    );
    if (routingTypeField) {
      accountRoutingType = routingTypeField.field.default || '';
    }

    const routingValueField = schema.data.fields.find(
      (field: FieldDefinition) => field.field.key === 'account_routing_value1'
    );
    if (routingValueField) {
      accountRoutingValue = routingValueField.field.default || '';
    }

    this.fields = schema.data.fields.filter(field => {
      return !hiddenFields.includes(field.field.key) &&
        field.enabled &&
        field.required;
    });

    const group: any = {};

    // Add required name fields at the start
    group['firstName'] = ['', [Validators.required]];
    group['lastName'] = ['', [Validators.required]];

    // Add hidden fields with default values
    group['bank_country_code'] = [this.selectedCountry, []];
    group['account_currency'] = [this.selectedCurrency, []];
    group['transfer_method'] = ['LOCAL', []];
    group['entity_type'] = ['PERSONAL', []];
    group['country_code'] = [this.selectedCountry, []];

    // Add schema-based fields if we found them

    if (localClearingSystem) {
      group['local_clearing_system'] = [localClearingSystem, [Validators.required]];
    }
    if (accountRoutingType) {
      group['account_routing_type1'] = [accountRoutingType, [Validators.required]];
    }
    if (accountRoutingValue) {
      group['account_routing_value1'] = [accountRoutingValue, [Validators.required]];
    }

    // Add visible fields
    this.fields.forEach(field => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }
      if (field.rule && field.rule.pattern) {
        validators.push(Validators.pattern(field.rule.pattern));
      }

      const defaultValue = field.field.default || '';
      group[field.field.key] = [defaultValue, validators];
    });

    this.fields = [
      {
        enabled: true,
        field: {
          key: 'firstName',
          type: 'INPUT',
          label: 'First Name',
          description: 'Your first name',
          placeholder: ''
        },
        path: 'beneficiary.first_name',
        required: true
      },
      {
        enabled: true,
        field: {
          key: 'lastName',
          type: 'INPUT',
          label: 'Last Name',
          description: 'Your last name',
          placeholder: ''
        },
        path: 'beneficiary.last_name',
        required: true
      },
      ...this.fields.filter(field => {
        return !hiddenFields.includes(field.field.key) &&
          field.enabled &&
          field.required;
      })
    ];

    //if fields contain a field with key postcode then make sure that is the last field
    const postcodeFieldIndex = this.fields.findIndex(field => field.field.key === 'postcode');
    if (postcodeFieldIndex !== -1) {
      const postcodeField = this.fields.splice(postcodeFieldIndex, 1)[0];
      this.fields.push(postcodeField);
    }

    this.dynamicForm = this.fb.group(group);
  }

  async onSubmit() {
    if (this.dynamicForm.valid) {
      this.isSubmitting = true;
      const formData = this.dynamicForm.value;
      const payload = this.createPayload();
      const isUpdate = this.router.url.includes('/update');

      try {
        // Get the current user's auth token
        const idToken = await this.authService.authToken().pipe(take(1)).toPromise();

        // Add idToken to payload
        const securePayload = {
          ...payload,
          idToken: idToken
        };

        const currentMerchant = await this.merchantStateStore.getMerchant(this.userId).pipe(take(1)).toPromise();

        let response;
        if (isUpdate && currentMerchant) {
          response = await this.http.put(
            environment.firebase.clouldFunctionHost + `/updateBeneficiary?merchantId=${currentMerchant.merchantId}`,
            securePayload
          ).toPromise();

        } else {
          response = await this.http.post(
            environment.firebase.clouldFunctionHost + `/createBeneficiary`,
            securePayload
          ).toPromise();

          if (response && response.merchant_id) {
            this.merchantStateStore.createMerchant(
              response.merchant_id,
              this.selectedCountry,
              this.selectedCurrency,
              this.userId
            );
          }
        }

        const registry = await this._registryStateStore.getState().pipe(take(1)).toPromise();
        registry.registry.country = this.selectedCountry;
        registry.registry.currency = this.selectedCurrency;
        await this._registryStateStore.updateState(registry);

        this.successMessage = isUpdate ? 'Payment details updated successfully' : 'Payment details added successfully';
        this.router.navigate(['/registry/add-payment-methods/credit-card']);
      } catch (error) {
        console.error('Error with beneficiary:', error);
        this.handleBeneficiaryError(error);
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  private createPayload(): BeneficiaryPayload {
    const formData = this.dynamicForm.value;

    const bankDetails: any = {
      bank_country_code: formData.bank_country_code,
      account_currency: formData.account_currency,
      account_number: formData.account_number,
      account_name: formData.account_name
    };

    // Add optional fields to bankDetails
    if (formData.account_routing_type1 && formData.account_routing_value1) {
      bankDetails.account_routing_type1 = formData.account_routing_type1;
      bankDetails.account_routing_value1 = formData.account_routing_value1;
    }
    if (formData.account_routing_value2) {
      bankDetails.account_routing_value2 = formData.account_routing_value2;
      bankDetails.account_routing_type2 = formData.account_routing_type2 || "transit_number";
    }
    if (formData.iban) {
      bankDetails.iban = formData.iban;
    }
    if (formData.local_clearing_system) {
      bankDetails.local_clearing_system = formData.local_clearing_system;
    }

    return {
      beneficiary: {
        entity_type: formData.entity_type,
        first_name: formData.firstName,
        last_name: formData.lastName,
        bank_details: bankDetails,
        address: {
          country_code: formData.country_code,
          postcode: formData.postcode,
          street_address: formData.street_address,
          city: formData.city,
          state: formData.state
        }
      },
      transfer_methods: ['LOCAL']
    };
  }

  private async loadUpdateMode(merchantId: string) {
    try {
      // 1. First get the beneficiary data with auth token
      const idToken = await this.authService.authToken().pipe(take(1)).toPromise();
      const beneficiaryResponse = await this.http.post<BeneficiaryResponse>(
        environment.firebase.clouldFunctionHost + `/getBeneficiary`,
        { merchantId, idToken }
      ).toPromise();

      console.log('Beneficiary data received:', beneficiaryResponse);

      // 2. Then fetch and generate the form
      await this.fetchSchema();

      // 3. Finally populate the form with the stored data
      if (beneficiaryResponse && beneficiaryResponse.data && beneficiaryResponse.data.beneficiary) {
        await this.populateForm(beneficiaryResponse.data.beneficiary);
      }
    } catch (error) {
      console.error('Error in update mode:', error);
      this.formErrors['general'] = 'Failed to load existing payment details';
      this.isLoading = false;
    }
  }

  private async populateForm(beneficiaryData: any) {
    // Populate bank details
    if (beneficiaryData.bank_details) {
      Object.keys(beneficiaryData.bank_details).forEach(key => {
        const control = this.dynamicForm.get(key);
        if (this.dynamicForm.contains(key) && control) {
          control.setValue(beneficiaryData.bank_details[key]);
        }
      });
    }

    // Get current merchant data for name fields
    const currentMerchant = await this.merchantStateStore.getMerchant(this.userId).pipe(take(1)).toPromise();

    // Populate name fields - prefer Firestore data over API data
    const firstNameControl = this.dynamicForm.get('firstName');
    const lastNameControl = this.dynamicForm.get('lastName');

    if (firstNameControl && currentMerchant) {
      firstNameControl.setValue(beneficiaryData.first_name || '');
    }
    if (lastNameControl && currentMerchant) {
      lastNameControl.setValue(beneficiaryData.last_name || '');
    }

    // Populate address fields
    if (beneficiaryData.address) {
      const addressFields = ['postcode', 'street_address', 'city', 'state'];
      addressFields.forEach(field => {
        if (this.dynamicForm.contains(field) && beneficiaryData.address[field]) {
          this.dynamicForm.get(field).setValue(beneficiaryData.address[field]);
        }
      });
    }

    // Populate other fields if they exist
    const otherFields = ['entity_type', 'account_currency', 'bank_country_code'];
    otherFields.forEach(field => {
      if (this.dynamicForm.contains(field) && beneficiaryData[field]) {
        this.dynamicForm.get(field).setValue(beneficiaryData[field]);
      }
    });
  }

  private handleBeneficiaryError(errorResponse: any) {
    // Handle authentication and authorization errors
    if (errorResponse && errorResponse.error) {
      const error = errorResponse.error;

      // Handle our new authentication errors
      if (error.code) {
        switch (error.code) {
          case 'MISSING_AUTH_TOKEN':
          case 'INVALID_AUTH_TOKEN':
          case 'AUTH_VERIFICATION_FAILED':
            this.errorMessage = 'Authentication failed. Please refresh the page and try again.';
            return;
          case 'MERCHANT_NOT_FOUND':
          case 'MERCHANT_ACCESS_DENIED':
          case 'MERCHANT_VALIDATION_ERROR':
            this.errorMessage = 'Access denied. Please contact support.';
            return;
          case 'MERCHANT_INVALID_STATE':
            this.errorMessage = 'Your account is not in the correct state for this operation. Please contact support.';
            return;
        }
      }
    }

    // Handle error structure from API
    if (errorResponse && errorResponse.error && errorResponse.error.data && errorResponse.error.data.data && errorResponse.error.data.data.details) {
      const apiErrors = errorResponse.error.data.data.details.errors;
      if (apiErrors && Array.isArray(apiErrors)) {
        apiErrors.forEach((err: any) => {
          if (!err || !err.source) {
            return;
          }

          // Extract field name from full path (e.g., "beneficiary.bank_details.account_routing_value1")
          const pathParts = err.source.split('.');
          const fieldName = pathParts[pathParts.length - 1];
          if (!fieldName) {
            return;
          }

          let errorMessage: string;
          switch (err.code) {
            case '010':
              errorMessage = 'Invalid routing number format';
              break;
            default:
              errorMessage = 'Invalid value';
          }

          // Set error for the form field
          this.formErrors[fieldName] = errorMessage;

          // Highlight the field in the form if it exists
          const control = this.dynamicForm.get(fieldName);
          if (control) {
            control.setErrors({ 'api': errorMessage });
            control.markAsTouched();
            // Only remove API error on value change, keep other validators
            control.valueChanges.subscribe(() => {
              const errors = control.errors;
              if (errors) {
                delete errors['api'];
                control.setErrors(Object.keys(errors).length ? errors : null);
              }
              delete this.formErrors[fieldName];
            });
          }
        });
        return;
      }
    }

    // Fallback for generic error
    this.formErrors['general'] = 'An error occurred while processing your request';
  }

  onCancel() {
    this.router.navigate(['/registry/add-payment-methods/credit-card']);
  }
} 


// WEBPACK FOOTER //
// ./src/app/merchant/merchant.component.ts